import { useState, useRef, useCallback, useMemo } from "react";
import "@/App.css";
import { 
  Play, Pause, Volume2, VolumeX, Maximize, SkipForward, SkipBack, 
  Loader2, Coffee, MapPin, Leaf, Wine, Globe, TrendingUp, RefreshCw, 
  ShoppingBag, Sparkles 
} from "lucide-react";
import { useScript, useChapters, useGenerations, useAudioPlayer } from "@/hooks/useVideoPlayer";
import { IntroCard, ChapterInfo, Subtitles, ChapterCard, formatTime } from "@/components/VideoComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARAKKAL_LOGO = "/arakkal-logo.png";

const CHAPTER_IMAGES = {
  "intro": "https://images.unsplash.com/photo-1649998344756-bb1e4cdd9e5c?w=1920",
  "hook": "https://images.unsplash.com/photo-1681366463291-30f169d575a2?w=1920",
  "the-place": "https://images.unsplash.com/photo-1762375398366-a2fb41563813?w=1920",
  "the-harvest": "https://images.unsplash.com/photo-1729627271876-91c490d2a080?w=1920",
  "tasting-notes": "https://images.unsplash.com/photo-1739050981003-6e2cfb081384?w=1920",
  "uk-connection": "https://images.unsplash.com/photo-1764042082382-d68eaeaa53f9?w=1920",
  "why-now": "https://images.unsplash.com/photo-1753572736794-1c6d1199aa50?w=1920",
  "cta": "https://images.unsplash.com/photo-1739303987875-db274ba45b60?w=1920"
};

const CHAPTER_ICONS = {
  "intro": Sparkles, "hook": Coffee, "the-place": MapPin, "the-harvest": Leaf,
  "tasting-notes": Wine, "uk-connection": Globe, "why-now": TrendingUp, "cta": ShoppingBag
};

function App() {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [loadingChapter, setLoadingChapter] = useState(null);
  
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const { script } = useScript();
  const { chapters } = useChapters();
  const { audioStatus, videoStatus, isGenerating, generateAllAudio, generateChapterAudio } = useGenerations();
  const audioPlayer = useAudioPlayer(chapters, currentChapter, audioStatus);

  const chapter = chapters[currentChapter];
  const hasVideo = chapter && videoStatus[chapter.id]?.status === "completed";
  const audioProcessing = chapter && audioStatus[chapter.id]?.status === "processing";
  
  const displayChapters = useMemo(() => chapters.filter(ch => !ch.is_intro), [chapters]);
  const allAudioReady = useMemo(
    () => displayChapters.every(ch => audioStatus[ch.id]?.status === "completed"),
    [displayChapters, audioStatus]
  );

  const goToChapter = useCallback((index) => {
    if (index >= 0 && index < chapters.length) {
      setCurrentChapter(index);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [chapters.length]);

  const handlePlayClick = useCallback(async () => {
    if (!chapter) return;

    if (chapter.is_intro) {
      goToChapter(currentChapter + 1);
      return;
    }

    if (!audioPlayer.hasAudio) {
      setLoadingChapter(chapter.id);
      await generateChapterAudio(chapter.id);
      setLoadingChapter(null);
      return;
    }

    audioPlayer.togglePlay();
    if (videoRef.current) {
      if (audioPlayer.isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [chapter, currentChapter, audioPlayer, generateChapterAudio, goToChapter]);

  const handleAudioEnded = useCallback(() => {
    audioPlayer.handleEnded();
    if (currentChapter < chapters.length - 1) {
      goToChapter(currentChapter + 1);
    }
  }, [audioPlayer, currentChapter, chapters.length, goToChapter]);

  const handleSeek = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    audioPlayer.seek(percentage);
    if (videoRef.current && audioPlayer.duration > 0) {
      videoRef.current.currentTime = percentage * audioPlayer.duration;
    }
  }, [audioPlayer]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }, []);

  const progress = audioPlayer.duration > 0 ? (audioPlayer.currentTime / audioPlayer.duration) * 100 : 0;
  const displayIndex = chapter ? displayChapters.findIndex(c => c.id === chapter.id) : -1;

  return (
    <div className="app" ref={containerRef} data-testid="video-player-app">
      <BackgroundLayer chapter={chapter} hasVideo={hasVideo} videoRef={videoRef} />

      <div className="main-content">
        <Header 
          logo={ARAKKAL_LOGO}
          allAudioReady={allAudioReady}
          isGenerating={isGenerating}
          onGenerateAll={() => generateAllAudio("fable")}
        />

        {chapter?.is_intro ? (
          <IntroCard onStart={() => goToChapter(1)} />
        ) : (
          <>
            <TitleSection />
            <PlayerContent 
              chapter={chapter}
              displayIndex={displayIndex}
              totalChapters={displayChapters.length}
              showSubtitles={showSubtitles}
              isPlaying={audioPlayer.isPlaying}
              hasAudio={audioPlayer.hasAudio}
              audioStatus={audioStatus}
              audioRef={audioPlayer.audioRef}
              onTimeUpdate={audioPlayer.handleTimeUpdate}
              onLoadedMetadata={audioPlayer.handleLoadedMetadata}
              onEnded={handleAudioEnded}
            />
          </>
        )}

        {!chapter?.is_intro && (
          <PlayerControls
            progress={progress}
            currentTime={audioPlayer.currentTime}
            duration={audioPlayer.duration}
            isPlaying={audioPlayer.isPlaying}
            isMuted={audioPlayer.isMuted}
            showSubtitles={showSubtitles}
            isLoading={audioProcessing || loadingChapter === chapter?.id}
            canGoPrev={currentChapter > 0}
            canGoNext={currentChapter < chapters.length - 1}
            onSeek={handleSeek}
            onPlayPause={handlePlayClick}
            onPrev={() => goToChapter(currentChapter - 1)}
            onNext={() => goToChapter(currentChapter + 1)}
            onToggleMute={audioPlayer.toggleMute}
            onToggleSubtitles={() => setShowSubtitles(prev => !prev)}
            onToggleFullscreen={toggleFullscreen}
          />
        )}

        <ChaptersGrid
          chapters={chapters}
          displayChapters={displayChapters}
          currentChapter={currentChapter}
          audioStatus={audioStatus}
          onSelectChapter={goToChapter}
        />

        {script && <DescriptionSection description={script.description} />}
      </div>
    </div>
  );
}

// Background component
function BackgroundLayer({ chapter, hasVideo, videoRef }) {
  const handleVideoError = (e) => { e.target.style.display = 'none'; };

  return (
    <div className="background-layer">
      {chapter && hasVideo && (
        <video
          ref={videoRef}
          className="background-video"
          src={`${API}/media/video/${chapter.id}`}
          loop muted playsInline autoPlay
          crossOrigin="anonymous"
          onError={handleVideoError}
          data-testid="background-video"
        />
      )}
      {chapter && (
        <div 
          className="background-image"
          style={{ backgroundImage: `url(${CHAPTER_IMAGES[chapter.id]})`, display: hasVideo ? 'none' : 'block' }}
          data-testid="background-image"
        />
      )}
      <div className={`background-overlay ${chapter?.is_intro ? 'intro-overlay' : ''}`} />
    </div>
  );
}

// Header component
function Header({ logo, allAudioReady, isGenerating, onGenerateAll }) {
  return (
    <header className="header" data-testid="header">
      <div className="logo">
        <img src={logo} alt="Arakkal Estate" className="logo-image" />
        <div className="logo-text">
          <span className="brand-name">Arakkal Estate</span>
          <span className="brand-tagline">Direct Trade · Single Origin · Arabica</span>
        </div>
      </div>
      <div className="header-actions">
        {allAudioReady ? (
          <span className="audio-ready-badge" data-testid="audio-ready-badge">Audio Ready</span>
        ) : (
          <button className="generate-btn" onClick={onGenerateAll} disabled={isGenerating} data-testid="generate-all-audio-btn">
            {isGenerating ? <><Loader2 className="spin" size={16} />Generating...</> : <><RefreshCw size={16} />Generate All Voiceovers</>}
          </button>
        )}
      </div>
    </header>
  );
}

// Title component
function TitleSection() {
  return (
    <div className="title-section" data-testid="title-section">
      <h1 className="main-title">From The Western Ghats to Your Cup</h1>
      <p className="subtitle">Arakkal Estate's Guide to India's Secret Coffee Region</p>
      <span className="brand-marque">Arakkal Estate</span>
    </div>
  );
}

// Player content component
function PlayerContent({ chapter, displayIndex, totalChapters, showSubtitles, isPlaying, hasAudio, audioStatus, audioRef, onTimeUpdate, onLoadedMetadata, onEnded }) {
  return (
    <div className="player-section" data-testid="player-section">
      {chapter && <ChapterInfo chapter={chapter} displayIndex={displayIndex} totalChapters={totalChapters} />}
      <Subtitles text={chapter?.script} isVisible={showSubtitles && isPlaying} />
      {chapter?.id === "cta" && <CTASection logo={ARAKKAL_LOGO} />}
      {chapter && hasAudio && (
        <audio
          ref={audioRef}
          key={`audio-${chapter.id}-${audioStatus[chapter.id]?.status}`}
          src={`${API}/media/audio/${chapter.id}`}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onLoadedMetadata}
          onEnded={onEnded}
          preload="auto"
          crossOrigin="anonymous"
          data-testid="audio-player"
        />
      )}
    </div>
  );
}

// CTA component
function CTASection({ logo }) {
  return (
    <div className="cta-section">
      <img src={logo} alt="Arakkal Estate" className="cta-logo" />
      <a href="https://www.everittinternational.com/partner" target="_blank" rel="noopener noreferrer" className="cta-button" data-testid="shop-cta-btn">
        <ShoppingBag size={20} />Buy Arakkal Estate Western Ghats coffee →
      </a>
    </div>
  );
}

// Player controls - split into sub-components
function PlayerControls(props) {
  const { progress, currentTime, duration, isPlaying, isMuted, showSubtitles, isLoading,
    canGoPrev, canGoNext, onSeek, onPlayPause, onPrev, onNext, onToggleMute, onToggleSubtitles, onToggleFullscreen } = props;

  return (
    <div className="controls-container" data-testid="controls-container">
      <ProgressBar progress={progress} onSeek={onSeek} />
      <div className="controls">
        <LeftControls 
          canGoPrev={canGoPrev} canGoNext={canGoNext} isLoading={isLoading} isPlaying={isPlaying}
          currentTime={currentTime} duration={duration}
          onPrev={onPrev} onNext={onNext} onPlayPause={onPlayPause}
        />
        <RightControls 
          isMuted={isMuted} showSubtitles={showSubtitles}
          onToggleMute={onToggleMute} onToggleSubtitles={onToggleSubtitles} onToggleFullscreen={onToggleFullscreen}
        />
      </div>
    </div>
  );
}

function ProgressBar({ progress, onSeek }) {
  return (
    <div className="progress-bar" onClick={onSeek} data-testid="progress-bar">
      <div className="progress-fill" style={{ width: `${progress}%` }} />
      <div className="progress-thumb" style={{ left: `${progress}%` }} />
    </div>
  );
}

function LeftControls({ canGoPrev, canGoNext, isLoading, isPlaying, currentTime, duration, onPrev, onNext, onPlayPause }) {
  const playIcon = isLoading ? <Loader2 className="spin" size={24} /> : isPlaying ? <Pause size={24} /> : <Play size={24} />;
  
  return (
    <div className="controls-left">
      <button className="control-btn" onClick={onPrev} disabled={!canGoPrev} data-testid="prev-chapter-btn"><SkipBack size={20} /></button>
      <button className="control-btn play-btn" onClick={onPlayPause} data-testid="play-pause-btn">{playIcon}</button>
      <button className="control-btn" onClick={onNext} disabled={!canGoNext} data-testid="next-chapter-btn"><SkipForward size={20} /></button>
      <span className="time-display" data-testid="time-display">{formatTime(currentTime)} / {formatTime(duration || 0)}</span>
    </div>
  );
}

function RightControls({ isMuted, showSubtitles, onToggleMute, onToggleSubtitles, onToggleFullscreen }) {
  return (
    <div className="controls-right">
      <button className="control-btn" onClick={onToggleMute} data-testid="mute-btn">{isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}</button>
      <button className="control-btn" onClick={onToggleSubtitles} data-testid="subtitles-toggle-btn"><span className={`subtitle-icon ${showSubtitles ? 'active' : ''}`}>CC</span></button>
      <button className="control-btn" onClick={onToggleFullscreen} data-testid="fullscreen-btn"><Maximize size={20} /></button>
    </div>
  );
}

// Chapters grid
function ChaptersGrid({ chapters, displayChapters, currentChapter, audioStatus, onSelectChapter }) {
  return (
    <div className="chapters-grid" data-testid="chapters-grid">
      {displayChapters.map((ch, index) => {
        const Icon = CHAPTER_ICONS[ch.id] || Coffee;
        const actualIndex = chapters.findIndex(c => c.id === ch.id);
        return (
          <ChapterCard
            key={ch.id}
            chapter={ch}
            index={index}
            isActive={actualIndex === currentChapter}
            hasAudio={audioStatus[ch.id]?.status === "completed"}
            isProcessing={audioStatus[ch.id]?.status === "processing"}
            imageUrl={CHAPTER_IMAGES[ch.id]}
            Icon={Icon}
            onClick={() => onSelectChapter(actualIndex)}
          />
        );
      })}
    </div>
  );
}

// Description section
function DescriptionSection({ description }) {
  return (
    <div className="description-section" data-testid="description-section">
      <p className="description-text">{description}</p>
      <div className="description-links">
        <a href="https://www.everittinternational.com/partner" target="_blank" rel="noopener noreferrer" className="description-link">
          <ShoppingBag size={16} />Shop Arakkal Estate Coffee
        </a>
      </div>
    </div>
  );
}

export default App;
